import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, Check, X, CalendarCheck,
  ShieldCheck, KeyRound, AlertTriangle,
} from "lucide-react";
import { Reveal } from "../components/ui-bits";
import { useApp } from "../context/AppContext";
import { IMAGES } from "../config/images";

const CARDS = [
  { img: IMAGES.hardWater, alt: "HARD WATER — Hard water leaves mineral deposits that can dull paint and damage the finish over time." },
  { img: IMAGES.stainsRust, alt: "WATER STAINS & RUST — Poor washing and improper drying can leave water marks, trapped moisture and conditions that contribute to corrosion." },
  { img: IMAGES.wastage, alt: "WATER WASTAGE — Traditional car washing can consume large amounts of water for a single vehicle. CARZA uses a water-efficient cleaning process designed to reduce unnecessary water usage." },
  { img: IMAGES.untrained, alt: "UNTRAINED SERVICE — Inconsistent cleaners, poor techniques and unreliable local centres can result in inconsistent quality and potential damage." },
];

const TRADITIONAL = ["Excessive water usage", "Hard-water residue", "Inconsistent quality", "Unverified cleaners", "No standardized process"];
const CARZA = [
  "Water-efficient process",
  "Cleaner finish with proper wipe & buff technique",
  "Trained & verified service partners",
  "Standardized SOP-driven service",
  "Convenient doorstep/society service",
  "Consistent customer experience",
];

const STEPS3 = [
  { icon: CalendarCheck, no: "1", title: "BOOK", desc: "Book CARZA from your phone." },
  { icon: ShieldCheck, no: "2", title: "CARE", desc: "A trained CARZA partner follows the standardized process." },
  { icon: KeyRound, no: "3", title: "DRIVE", desc: "Get a cleaner, better-finished car without the hassle of traditional washing." },
];

export default function BiggerProblems() {
  const { openBooking } = useApp();
  return (
    <section id="bigger-problems" className="light-section relative pb-20 lg:pb-28" data-testid="bigger-problems-section">
      <div className="dot-grid-light pointer-events-none absolute inset-0" />

      {/* headline */}
      <div className="container-x relative pt-20 lg:pt-24">
        <Reveal><span className="eyebrow eyebrow-dark">The Real Problem</span></Reveal>
        <Reveal delay={0.06}>
          <h2 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-nighttext sm:text-5xl lg:text-6xl" data-testid="bigger-problems-headline">
            Your Car Has <span className="text-carza">Bigger Problems</span> Than Dirt.
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#5c5470] sm:text-lg" data-testid="bigger-problems-sub">
            Hard water. Water stains. Rusting. Wasted water. Untrained cleaners. CARZA is built to solve them.
          </p>
        </Reveal>
      </div>

      {/* 4 problem cards */}
      <div className="container-x relative mt-12 grid grid-cols-1 gap-6 lg:max-w-5xl lg:grid-cols-2 lg:gap-8 lg:px-8 xl:px-0">
        {CARDS.map((c, i) => (
          <Reveal key={c.alt} delay={i * 0.08}>
            <motion.div whileHover={{ y: -6 }} transition={{ type: "spring", stiffness: 260, damping: 22 }} className="overflow-hidden rounded-3xl bg-white shadow-[0_25px_60px_-30px_rgba(23,19,31,0.45)]" data-testid={`bigger-problem-card-${i}`}>
              <img src={c.img} alt={c.alt} loading="lazy" className="aspect-[769/526] w-full object-cover" />
            </motion.div>
          </Reveal>
        ))}
      </div>

      {/* transformation band */}
      <div className="container-x relative mt-16">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#241543] via-carza-deep to-[#1b0f33] p-7 shadow-[0_40px_90px_-45px_rgba(109,61,245,0.6)] sm:p-10 lg:p-12" data-testid="transformation-band">
            <div className="dot-grid pointer-events-none absolute inset-0 opacity-70" />
            <div className="relative">
              <Reveal>
                <p className="text-center font-display text-2xl font-bold tracking-tight text-paper sm:text-3xl lg:text-4xl" data-testid="carza-changes-line">
                  CARZA <span className="text-carza-lite">CHANGES THE WAY</span> YOUR CAR IS CARED FOR.
                </p>
              </Reveal>

              <div className="mt-10 grid grid-cols-1 items-center gap-6 lg:grid-cols-12">
                {/* traditional */}
                <Reveal delay={0.08} className="lg:col-span-5">
                  <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6" data-testid="transformation-traditional">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist-2">Traditional car wash</p>
                    <ul className="mt-5 flex flex-col gap-3.5">
                      {TRADITIONAL.map((t) => (
                        <li key={t} className="flex items-start gap-3 text-[14px] text-mist">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/[0.07] text-[#9a8fb5]"><X size={11} /></span>
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>

                {/* arrow */}
                <Reveal delay={0.12} className="flex justify-center lg:col-span-2">
                  <motion.span
                    initial={{ x: -8 }} whileInView={{ x: 0 }} viewport={{ once: true }}
                    transition={{ duration: 1, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                    className="flex h-14 w-14 items-center justify-center rounded-full bg-carza text-white shadow-[0_0_40px_rgba(124,77,255,0.7)]"
                    data-testid="transformation-arrow"
                  >
                    <ArrowRight size={22} className="lg:hidden" />
                    <ArrowDown size={22} className="hidden lg:block" />
                  </motion.span>
                </Reveal>

                {/* carza */}
                <Reveal delay={0.16} className="lg:col-span-5">
                  <div className="rounded-3xl border border-carza/40 bg-carza/[0.08] p-6 shadow-[0_20px_60px_-30px_rgba(124,77,255,0.6)]" data-testid="transformation-carza">
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-carza-lite">
                      <span className="h-2 w-2 rounded-full bg-carza-lite" /> The Carza way
                    </p>
                    <ul className="mt-5 flex flex-col gap-3.5">
                      {CARZA.map((t) => (
                        <li key={t} className="flex items-start gap-3 text-[14px] font-medium text-paper">
                          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-carza text-white"><Check size={11} /></span>
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              </div>

              {/* USP statement */}
              <Reveal delay={0.1} className="mt-12 text-center">
                <p className="mx-auto max-w-3xl font-display text-3xl font-bold tracking-tight text-paper sm:text-4xl lg:text-5xl" data-testid="usp-statement">
                  Less Water. <span className="text-carza-lite">Better Process.</span> Consistent Care.
                </p>
                <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-mist sm:text-base" data-testid="usp-supporting">
                  CARZA combines a water-efficient cleaning process, trained service partners and standardized operating procedures to make professional car care more reliable, convenient and sustainable.
                </p>
              </Reveal>

              {/* 3-step visual */}
              <div className="relative mt-12 grid grid-cols-1 gap-8 sm:grid-cols-3 lg:gap-6">
                <div className="pointer-events-none absolute left-[16%] right-[16%] top-8 hidden border-t-2 border-dashed border-carza/30 sm:block" aria-hidden="true" />
                {STEPS3.map((s, i) => (
                  <Reveal key={s.title} delay={0.1 + i * 0.08} className="relative">
                    <div className="flex flex-col items-center gap-3 text-center" data-testid={`bigger-steps-${i + 1}`}>
                      <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-carza/35 bg-[#160b2e] text-carza-lite">
                        <s.icon size={24} />
                        <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-carza font-mono text-[10px] font-bold text-white">{s.no}</span>
                      </span>
                      <h3 className="font-mono text-[12px] font-bold uppercase tracking-[0.28em] text-paper">{s.title}</h3>
                      <p className="mx-auto max-w-[250px] text-[13px] leading-relaxed text-mist">{s.desc}</p>
                    </div>
                  </Reveal>
                ))}
              </div>

              {/* urgency CTA */}
              <Reveal delay={0.15} className="mt-12">
                <div className="flex flex-col items-center gap-5 rounded-3xl border border-amber-400/25 bg-amber-400/[0.07] px-6 py-7 text-center" data-testid="urgency-cta">
                  <p className="flex items-start justify-center gap-2.5 text-[15px] font-medium leading-relaxed text-paper/90 sm:text-base">
                    <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-300" />
                    Don't wait for stains, corrosion or poor cleaning to become expensive problems.
                  </p>
                  <button onClick={() => openBooking()} className="btn btn-primary px-9 py-4 text-[15px]" data-testid="bigger-problems-book-btn">
                    Book CARZA <ArrowRight size={16} />
                  </button>
                </div>
              </Reveal>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
